import axios, {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
  HttpStatusCode,
  type InternalAxiosRequestConfig,
} from 'axios';
import queryString from 'query-string';

import {
  type HttpMethod,
  type ObjectLike,
  type QueryParameters,
  createClient,
} from '@api-client/generated/client';
import {
  classify401,
  endSession,
  ensureFreshToken,
  getBearerToken,
} from '@auth';
import { env } from '@utils/env';

type AxiosConfig = {
  requestBody?: ObjectLike;
  httpMethod?: HttpMethod;
  queryParameters?: QueryParameters;
} & InternalAxiosRequestConfig;

const createUrlWithQueryParameters = (
  url: string = '',
  parameters: QueryParameters
) => {
  const transformedParams: Record<string, unknown | undefined> = {};

  Object.keys(parameters).map((key) => {
    transformedParams[key] = parameters[key].value;
  });

  return `${url}?${queryString.stringify(transformedParams, {
    arrayFormat: 'comma',
  })}`;
};

axios.interceptors.request.use(
  async (config: AxiosConfig) => {
    const { headers, url, httpMethod, requestBody, queryParameters } = config;

    // Refresh before reading the token, in this one place, so no call site has to think about it.
    await ensureFreshToken();

    const token = getBearerToken();

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    // The `companyid` header the generated client sets per operation is left alone: the tenant is
    // not a token claim, and the backend resolves the company from that header.
    return {
      ...config,
      url: queryParameters
        ? createUrlWithQueryParameters(url, queryParameters)
        : url,
      method: httpMethod,
      data: requestBody,
    };
  },
  (error) => Promise.reject(error)
);

axios.interceptors.response.use(
  async (response) => response,
  async (error) => {
    if (error.response?.status === HttpStatusCode.Unauthorized) {
      // The token was already refreshed on the way out, so a 401 means the identity itself is not
      // valid to this backend. endSession navigates exactly once and never retries — an unlinked
      // identity goes to a dead-end page rather than back through Keycloak, which would loop.
      endSession(classify401(error.response?.data));
    }

    return Promise.reject(error);
  }
);

/**
 * @description Using the client API without a hook
 *
 * @example
 *
 * apiClient.AccountsController_getAccounts({
 *   parameter: {
 *    companyId: 'c046d1b3-0c52-4b01-af9e-b2320d0b34b2',
 *   },
 * })
 * .then((response) => {
 *   // Response handler
 * })
 * .catch((error) => {
 *   // Error handler
 * });
 *
 */

class AxiosProxy {
  async request<T = unknown, D = unknown>(
    config: AxiosRequestConfig<D>,
    options?: AxiosRequestConfig
  ): Promise<T> {
    return axios
      .request<T, AxiosResponse<T>, D>({ ...config, ...options })
      .then((res) => {
        if (axios.isAxiosError(res)) {
          const axiosError: AxiosError = res;
          throw axiosError;
        } else {
          return res.data;
        }
      });
  }
}

const apiClient = createClient(
  new AxiosProxy(),
  env.VITE_ROUTE_API_BASE_URL || ''
);

export default apiClient;
